import { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, PlusCircle, FolderKanban, User, Bell, LogOut, X, Download, Rss, Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguageContext";
import { useInstall } from "../../context/InstallContext";
import Logo from "../../components/Logo";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import UserAvatar from "../../components/UserAvatar";
import LegalStrip from "../../components/LegalStrip";
import ThemeSection from "../../components/ThemeToggle";

const NAV = [
  { to: "/app", tk: "u_nav_dash", icon: LayoutDashboard, end: true },
  { to: "/app/deposer", tk: "u_nav_deposit", icon: PlusCircle },
  { to: "/app/projets", tk: "u_nav_projects", icon: FolderKanban },
  { to: "/app/profil", tk: "u_nav_profile", icon: User },
  { to: "/blog", tk: "u_nav_blog", icon: Rss },
];

export default function AppLayout() {
  const { user, setUser, logout } = useAuth();
  const { t } = useLang();
  const { open: openInstall, canInstall } = useInstall();
  const nav = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => { loadNotifs(); }, []);
  const unread = notifs.filter((n) => !n.read).length;

  const openNotifs = async () => {
    setOpen(!open);
    if (!open && unread) { await api.post("/notifications/read"); setTimeout(loadNotifs, 400); }
  };

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Image trop lourde (max 2 Mo).");
    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      try {
        const { data } = await api.put("/auth/avatar", { image: reader.result });
        setUser(data);
        toast.success("Photo de profil mise à jour.");
        setMenu(false);
      } catch (err) { toast.error(apiErr(err)); }
      finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  const removePhoto = async () => {
    setUploading(true);
    try {
      const { data } = await api.delete("/auth/avatar");
      setUser(data);
      toast.success("Photo supprimée.");
      setMenu(false);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); }
  };

  return (
    <div className="grain flex min-h-screen bg-void">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-white/10 bg-surface lg:flex">
        <Link to="/" className="border-b border-white/10 px-6 py-5"><Logo size={28} /></Link>
        <nav className="flex-1 space-y-1 overflow-y-auto p-4">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`app-nav-${n.to.split("/").pop() || "dash"}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-3 text-sm transition-all ${isActive ? "border-l-2 border-cyan bg-cyan/5 text-cyan" : "border-l-2 border-transparent text-ink-secondary hover:text-white"}`}>
              <n.icon size={18} /> {t(n.tk)}
            </NavLink>
          ))}
        </nav>
        {canInstall && (
          <button onClick={openInstall} data-testid="app-install-app" className="flex items-center gap-3 border-t border-white/10 px-6 py-4 text-sm text-cyan transition-colors hover:text-white">
            <Download size={18} /> {t("pwa_menu")}
          </button>
        )}
        <ThemeSection testid="app-theme" />
        <button onClick={() => { logout(); nav("/"); }} data-testid="app-logout" className="flex items-center gap-3 border-t border-white/10 px-6 py-4 text-sm text-ink-secondary transition-colors hover:text-white">
          <LogOut size={18} /> {t("u_logout")}
        </button>
      </aside>

      <div className="min-w-0 flex-1 lg:pl-64">
        <header className="glass sticky top-0 z-20 flex items-center justify-between border-b border-white/10 px-5 py-4 sm:px-8">
          <div className="lg:hidden"><Logo size={26} withText={false} /></div>
          <div className="hidden text-sm text-ink-secondary lg:block">{t("u_hello")}, <span className="text-white">{user?.first_name}</span></div>
          <div className="relative flex items-center gap-3">
            <LanguageSwitcher />
            <button onClick={openNotifs} data-testid="notif-bell" className="relative p-2 text-ink-secondary transition-colors hover:text-white">
              <Bell size={20} />
              {unread > 0 && <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-cyan" />}
            </button>

            {user && (
              <div className="relative">
                <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="header-avatar-input" />
                <button onClick={() => setMenu((m) => !m)} data-testid="header-avatar-button"
                  className="rounded-full ring-2 ring-transparent transition-all hover:ring-cyan/50">
                  <UserAvatar user={user} size={38} testid="header-avatar" />
                </button>
                {menu && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setMenu(false)} />
                    <div className="absolute right-0 top-12 z-40 w-56 overflow-hidden rounded-xl border border-white/10 bg-elevated shadow-2xl" data-testid="header-avatar-menu">
                      <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
                        <UserAvatar user={user} size={40} />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-600 text-white">{user.first_name} {user.last_name}</div>
                          <div className="truncate text-[11px] text-ink-tertiary">{user.email}</div>
                        </div>
                      </div>
                      <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="header-avatar-change"
                        className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-white transition-colors hover:bg-cyan/10 disabled:opacity-50">
                        <Camera size={16} className="text-cyan" /> {uploading ? "Chargement…" : (user.photo ? "Changer la photo" : "Ajouter une photo")}
                      </button>
                      {user.photo && (
                        <button onClick={removePhoto} disabled={uploading} data-testid="header-avatar-delete"
                          className="flex w-full items-center gap-3 border-t border-white/5 px-4 py-3 text-left text-sm text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-50">
                          <Trash2 size={16} /> Supprimer la photo
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="flex gap-1 lg:hidden">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `p-2 ${isActive ? "text-cyan" : "text-ink-secondary"}`}>
                  <n.icon size={19} />
                </NavLink>
              ))}
              <button onClick={() => { logout(); nav("/"); }} className="p-2 text-ink-secondary"><LogOut size={19} /></button>
            </div>
            {open && (
              <div className="absolute right-0 top-12 z-40 w-80 border border-white/10 bg-elevated shadow-2xl" data-testid="notif-panel">
                <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                  <span className="text-sm font-600 text-white">{t("u_notifications")}</span>
                  <button onClick={() => setOpen(false)}><X size={16} className="text-ink-secondary" /></button>
                </div>
                <div className="max-h-96 overflow-y-auto no-scrollbar">
                  {notifs.length === 0 && <p className="px-4 py-6 text-sm text-ink-tertiary">{t("u_no_notif")}</p>}
                  {notifs.map((n) => (
                    <div key={n.id} className={`border-b border-white/5 px-4 py-3 text-sm ${n.read ? "text-ink-secondary" : "text-white"}`}>
                      {n.text}
                      <div className="mt-1 text-[11px] text-ink-tertiary">{new Date(n.at).toLocaleString("fr-FR")}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </header>
        <div className="p-5 sm:p-8"><Outlet /></div>
        <LegalStrip />
      </div>
    </div>
  );
}
